import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "input", "iterations", "pendingPrompt", "textarea", "indicator", "form", "errorContainer", "errorText", "statusText" ]

  connect() {
    this.boundHideProcessing = this.hideProcessing.bind(this)
    this.boundRestoreState = this.restoreState.bind(this)

    if (this.conversationId() !== 'global') {
      this.restoreState()
      this.scrollToBottom()
    }
    
    // Listen for CableReady updates on the document
    document.addEventListener("cable-ready:after-update", this.boundHideProcessing)
    
    // Also listen for Turbo frame/page loads
    document.addEventListener("turbo:load", this.boundRestoreState)

    // Escuta evento customizado para esconder processamento
    document.addEventListener("conversation:finished", this.boundHideProcessing)

    // Adiciona evento para capturar erros de rede/servidor no Turbo
    document.addEventListener("turbo:submit-end", this.handleResponse.bind(this))
  }

  disconnect() {
    document.removeEventListener("cable-ready:after-update", this.boundHideProcessing)
    document.removeEventListener("turbo:load", this.boundRestoreState)
    document.removeEventListener("conversation:finished", this.boundHideProcessing)
    document.removeEventListener("turbo:submit-end", this.handleResponse.bind(this))
  }

  showProcessing() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.remove('waiting-external')
      if (this.hasStatusTextTarget) {
        // Pega o texto do dataset se disponível, senão usa o padrão
        this.statusTextTarget.textContent = this.statusTextTarget.dataset.processingText || "Processando"
      }
      const prompt = this.hasTextareaTarget ? this.textareaTarget.value : ""
      
      this.indicatorTarget.classList.remove("hidden")
      if (this.hasErrorContainerTarget) {
        this.errorContainerTarget.classList.add("hidden")
      }
      localStorage.setItem(`processing-${this.conversationId()}`, "true")
      localStorage.setItem(`prompt-${this.conversationId()}`, prompt)

      // Limpa o textarea imediatamente após capturar o valor para o indicador
      if (this.hasTextareaTarget) {
        this.textareaTarget.value = ""
      }
    }
  }

  handleResponse(event) {
    const success = event.detail.success
    const response = event.detail.fetchResponse

    if (success) {
      // Não escondemos o processamento aqui, pois o streaming continuará via ActionCable.
      // O hideProcessing será chamado pelo evento 'conversation:finished' vindo do servidor.
      if (this.hasFormTarget) {
        this.formTarget.reset()
      }
      if (this.hasTextareaTarget) {
        this.textareaTarget.value = ""
      }
    } else {
      this.showError(response)
    }
  }

  async showError(fetchResponse) {
    this.indicatorTarget.classList.add("hidden")
    localStorage.removeItem(`processing-${this.conversationId()}`)
    
    let errorMessage = "Ocorreu um erro ao processar sua solicitação."
    
    try {
      const data = await fetchResponse.response.json()
      if (data.error) {
        errorMessage = data.error
      } else if (data.base) {
        errorMessage = data.base.join(", ")
      }
    } catch (e) {
      console.error("Erro ao analisar resposta de erro:", e)
    }

    if (this.hasErrorContainerTarget && this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = errorMessage
      this.errorContainerTarget.classList.remove("hidden")
    }
  }

  retry() {
    if (this.hasFormTarget) {
      this.formTarget.requestSubmit()
    }
  }

  submitOnEnter(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      if (this.hasFormTarget) {
        this.formTarget.requestSubmit()
      }
    }
  }

  showProcessingForNew() {
    const prompt = this.hasTextareaTarget ? this.textareaTarget.value : ""
    localStorage.setItem("processing-new-conversation", "true")
    localStorage.setItem("prompt-new-conversation", prompt)
  }

  restoreState() {
    // If we are not on a conversation page anymore, don't do anything
    if (this.conversationId() === 'global') return

    let id = this.conversationId()
    let isProcessing = localStorage.getItem(`processing-${id}`) === "true"
    let savedPrompt = localStorage.getItem(`prompt-${id}`)

    console.log("restoreState para id:", id, "isProcessing:", isProcessing)

    // Se carregou uma conversa específica e NÃO estamos em uma ação de submit, limpa estados globais
    // Isso evita que o "Processing..." fique travado após um redirecionamento de criação
    if (id !== 'new') {
      localStorage.removeItem("processing-new-conversation")
      localStorage.removeItem("prompt-new-conversation")
      
      // Se acabamos de chegar na página (não via submit AJAX), limpa o processamento desta conversa
      // O Turbo disparará o evento de finalização se for uma iteração, mas para o show inicial limpamos aqui
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        localStorage.removeItem(`processing-${id}`)
        localStorage.removeItem(`prompt-${id}`)
        isProcessing = false
      }
    }

    // Check if there's a pending new conversation processing
    if (!isProcessing && id !== 'new') {
      const isNewProcessing = localStorage.getItem("processing-new-conversation") === "true"
      if (isNewProcessing) {
        isProcessing = true
        savedPrompt = localStorage.getItem("prompt-new-conversation")
        
        // Transfer to this specific conversation ID
        localStorage.setItem(`processing-${id}`, "true")
        localStorage.setItem(`prompt-${id}`, savedPrompt)
        
        // Clear global
        localStorage.removeItem("processing-new-conversation")
        localStorage.removeItem("prompt-new-conversation")
      }
    }

    if (isProcessing) {
      if (this.hasIndicatorTarget) {
        this.indicatorTarget.classList.remove("hidden")
      }
    } else {
      this.hideProcessing()
    }
  }

  conversationId() {
    const pathParts = window.location.pathname.split('/')
    const index = pathParts.indexOf('conversations')
    return index !== -1 ? pathParts[index + 1] : 'global'
  }

  // This is called when the page is updated via CableReady or Turbo Stream
  hideProcessing() {
    if (this.conversationId() === 'global') return

    console.log("hideProcessing chamado para id:", this.conversationId())
    
    // Remove as mensagens temporárias de streaming se houver
    // Mas apenas se elas já foram substituídas pela iteração real
    // Ou podemos simplesmente deixar o streaming container lá e o Turbo Stream do broadcast_append_to cuidará.
    // No entanto, se o streaming terminou, queremos limpar o local storage
    
    if (this.hasIndicatorTarget) {
      // Se ainda estivermos aguardando resposta externa no servidor, não escondemos o indicador
      // Isso será controlado pelo Turbo Stream vindo do webhook
      if (this.indicatorTarget.classList.contains('waiting-external')) {
        console.log("Mantendo indicador visível pois está aguardando resposta externa")
        // No caso de waiting external, ainda queremos resetar o form se o submit foi bem sucedido
        return
      }

      this.indicatorTarget.classList.add("hidden")
      localStorage.removeItem(`processing-${this.conversationId()}`)
      localStorage.removeItem(`prompt-${this.conversationId()}`)
    }
    
    // Pequeno delay para garantir que o Turbo Stream terminou de renderizar
    setTimeout(() => {
      this.scrollToBottom()
    }, 100)
  }

  scrollToBottom() {
    if (this.conversationId() === 'global') return
    
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth'
    })
  }
}
